import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WiwInvestigationHeader from '../components/layout/WiwInvestigationHeader';
import Footer from '../components/layout/Footer';
import InvestigationResultsPanel from '../components/investigation/InvestigationResultsPanel';
import InvestigationStatsBar from '../components/investigation/InvestigationStatsBar';
import ScanRadarOverlay from '../components/investigation/ScanRadarOverlay';
import FloatingScanHud from '../components/investigation/FloatingScanHud';
import ReportModal from '../components/ReportModal';
import useInvestigationWorkspace from '../hooks/useInvestigationWorkspace';
import HomeHeroStrip from './home/HomeHeroStrip';
import HomeModuleGrid from './home/HomeModuleGrid';
import cyberTracerLogo from '../assets/cyber-tracer-logo-header.png';
import {
  CORPORATE_DOMAIN_DNS_HINT,
  companyDomainFromQuery,
  detectInput,
  EMAIL_DOMAIN_DNS_HINT,
  isInsufficientSingleTermQuery,
  isValidDomainHost,
  TAB_COMPANY,
  TAB_EMAIL,
  TAB_PERSON,
  validateCompanyDomainSyntax,
} from './home/homeSearchUtils';
import { deepLinkOptsFromPivot, parseInvestigationDeepLink } from '../utils/investigationDeepLink';
import { checkDomainNS, emailDomainFromAddress } from '../utils/checkDomainNS';
import { beginSearchTrace, markSearchTrace } from '../utils/searchTraceClient';
import {
  buildPersonPillsFromQuery,
  buildPersonPillsFromTags,
  composeQueryFromPills,
  cyclePillById,
  pillsToPersonTags,
  removePillById,
  setPillTypeById,
  shouldMaterializePersonPills,
} from '../utils/personSearchPills';
import {
  buildCorporatePillsFromQuery,
  composeQueryFromCorporatePills,
  cycleCorporatePillById,
  pillsToCorporateTags,
  removeCorporatePillById,
  setCorporatePillTypeById,
  shouldMaterializeCorporatePills,
} from '../utils/corporateSearchPills';
import './Home.css';

function normalizeWhitespaceJoin(...parts) {
  return parts
    .map((p) => String(p || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(' ')
    .trim();
}

export default function Home() {
  const ws = useInvestigationWorkspace();
  const [query, setQuery] = useState('');
  const [detection, setDetection] = useState(null);
  const [moduleOverride, setModuleOverride] = useState(null);
  const [isManualOverride, setIsManualOverride] = useState(false);
  /** @type {[{ id: string, type: string, text: string }[] | null, Function]} */
  const [heroPills, setHeroPills] = useState(null);
  /** @type {['person' | 'company' | null, Function]} */
  const [heroPillKind, setHeroPillKind] = useState(null);
  const [pillDraft, setPillDraft] = useState('');
  const heroPillsRef = useRef(null);
  const heroPillKindRef = useRef(null);
  const pillDraftRef = useRef('');
  const queryRef = useRef('');
  const [companyDomainCheck, setCompanyDomainCheck] = useState({
    syntaxValid: null,
    nsResolved: null,
    checking: false,
    host: '',
    message: '',
  });
  const [emailDomainCheck, setEmailDomainCheck] = useState({
    nsResolved: null,
    checking: false,
    host: '',
    message: '',
  });
  const heroSubmitInFlightRef = useRef(false);

  const syncQueryDetection = useCallback((nextQuery) => {
    const next = String(nextQuery || '');
    const trimmed = next.trim();
    setQuery(next);
    queryRef.current = next;
    if (!trimmed) {
      setIsManualOverride(false);
      setModuleOverride(null);
      setDetection(null);
      setEmailDomainCheck({
        nsResolved: null,
        checking: false,
        host: '',
        message: '',
      });
      return;
    }
    setDetection(detectInput(trimmed));
  }, []);

  const clearHeroPills = useCallback(() => {
    setHeroPills(null);
    heroPillsRef.current = null;
    setHeroPillKind(null);
    heroPillKindRef.current = null;
    setPillDraft('');
    pillDraftRef.current = '';
  }, []);

  const handleQueryChange = (value) => {
    clearHeroPills();
    syncQueryDetection(value);
  };

  const handlePillDraftChange = (value) => {
    const next = String(value ?? '');
    setPillDraft(next);
    pillDraftRef.current = next;
  };

  const applyHeroPills = useCallback(
    (nextPills, kind) => {
      const list = Array.isArray(nextPills) ? nextPills : [];
      if (list.length === 0) {
        clearHeroPills();
        return { pills: null, composedQuery: '', tags: null, kind: null };
      }
      setHeroPills(list);
      heroPillsRef.current = list;
      setHeroPillKind(kind);
      heroPillKindRef.current = kind;
      const composed =
        kind === 'company'
          ? composeQueryFromCorporatePills(list)
          : composeQueryFromPills(list);
      syncQueryDetection(composed);
      setPillDraft('');
      pillDraftRef.current = '';
      const tags =
        kind === 'company' ? pillsToCorporateTags(list) : pillsToPersonTags(list);
      return { pills: list, composedQuery: composed, tags, kind };
    },
    [clearHeroPills, syncQueryDetection],
  );

  const materializeHeroPills = useCallback(
    ({ force = false, draft, sourceText, module } = {}) => {
      const existing = heroPillsRef.current;
      const existingKind = heroPillKindRef.current;
      const draftText =
        draft !== undefined
          ? String(draft || '').trim()
          : String(pillDraftRef.current || '').trim();

      const resolvedModule =
        module ||
        (isManualOverride && moduleOverride) ||
        detectInput(String(sourceText || queryRef.current || '').trim())?.tab ||
        null;

      if (sourceText !== undefined) {
        const source = String(sourceText || '').trim();
        if (!source) return { pills: null, composedQuery: '', tags: null, kind: null };
        if (resolvedModule === TAB_COMPANY || shouldMaterializeCorporatePills(source)) {
          if (!shouldMaterializeCorporatePills(source)) {
            return { pills: null, composedQuery: source, tags: null, kind: null };
          }
          return applyHeroPills(buildCorporatePillsFromQuery(source), 'company');
        }
        if (!shouldMaterializePersonPills(source)) {
          return { pills: null, composedQuery: source, tags: null, kind: null };
        }
        return applyHeroPills(buildPersonPillsFromQuery(source), 'person');
      }

      if (Array.isArray(existing) && existing.length > 0 && existingKind) {
        if (!draftText) {
          const composed =
            existingKind === 'company'
              ? composeQueryFromCorporatePills(existing)
              : composeQueryFromPills(existing);
          const tags =
            existingKind === 'company'
              ? pillsToCorporateTags(existing)
              : pillsToPersonTags(existing);
          return { pills: existing, composedQuery: composed, tags, kind: existingKind };
        }
        const merged = normalizeWhitespaceJoin(
          existingKind === 'company'
            ? composeQueryFromCorporatePills(existing)
            : composeQueryFromPills(existing),
          draftText,
        );
        if (existingKind === 'company') {
          return applyHeroPills(buildCorporatePillsFromQuery(merged), 'company');
        }
        return applyHeroPills(buildPersonPillsFromQuery(merged), 'person');
      }

      const source = String(queryRef.current || '').trim();
      if (!source) {
        return { pills: null, composedQuery: '', tags: null, kind: null };
      }

      if (resolvedModule === TAB_COMPANY) {
        if (!shouldMaterializeCorporatePills(source)) {
          return { pills: null, composedQuery: source, tags: null, kind: null };
        }
        return applyHeroPills(buildCorporatePillsFromQuery(source), 'company');
      }

      if (resolvedModule === TAB_PERSON) {
        if (!shouldMaterializePersonPills(source)) {
          return { pills: null, composedQuery: source, tags: null, kind: null };
        }
        return applyHeroPills(buildPersonPillsFromQuery(source), 'person');
      }

      // Auto-detect compound corporate (domain + extras) even if tab not locked yet.
      if (shouldMaterializeCorporatePills(source)) {
        return applyHeroPills(buildCorporatePillsFromQuery(source), 'company');
      }
      if (force && shouldMaterializePersonPills(source)) {
        return applyHeroPills(buildPersonPillsFromQuery(source), 'person');
      }
      return { pills: null, composedQuery: source, tags: null, kind: null };
    },
    [applyHeroPills, isManualOverride, moduleOverride],
  );

  const handleCyclePillType = useCallback(
    (pillId) => {
      const current = heroPillsRef.current || [];
      const kind = heroPillKindRef.current;
      if (kind === 'company') {
        applyHeroPills(cycleCorporatePillById(current, pillId), 'company');
        return;
      }
      applyHeroPills(cyclePillById(current, pillId), 'person');
    },
    [applyHeroPills],
  );

  const handleSetPillType = useCallback(
    (pillId, nextType) => {
      const current = heroPillsRef.current || [];
      const kind = heroPillKindRef.current;
      if (kind === 'company') {
        applyHeroPills(setCorporatePillTypeById(current, pillId, nextType), 'company');
        return;
      }
      applyHeroPills(setPillTypeById(current, pillId, nextType), 'person');
    },
    [applyHeroPills],
  );

  const handleRemovePill = useCallback(
    (pillId) => {
      const current = heroPillsRef.current || [];
      const kind = heroPillKindRef.current;
      const next =
        kind === 'company'
          ? removeCorporatePillById(current, pillId)
          : removePillById(current, pillId);
      if (next.length === 0) {
        const composed =
          kind === 'company'
            ? composeQueryFromCorporatePills(current.filter((p) => p.id !== pillId))
            : composeQueryFromPills(current.filter((p) => p.id !== pillId));
        clearHeroPills();
        if (composed) syncQueryDetection(composed);
        return;
      }
      applyHeroPills(next, kind === 'company' ? 'company' : 'person');
    },
    [applyHeroPills, clearHeroPills, syncQueryDetection],
  );

  const detectedTab = detection?.tab ?? null;

  const previewModule = useMemo(() => {
    if (isManualOverride && moduleOverride) return moduleOverride;
    if (!query.trim()) return null;
    return detectedTab || null;
  }, [query, isManualOverride, moduleOverride, detectedTab]);

  useEffect(() => {
    if (previewModule !== TAB_COMPANY || !query.trim()) {
      setCompanyDomainCheck({
        syntaxValid: null,
        nsResolved: null,
        checking: false,
        host: '',
        message: '',
      });
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const { valid, host, message } = validateCompanyDomainSyntax(query);
      setCompanyDomainCheck({
        syntaxValid: valid,
        nsResolved: null,
        checking: valid,
        host,
        message,
      });

      if (!valid) return;

      checkDomainNS(host).then((ok) => {
        setCompanyDomainCheck((prev) => {
          if (prev.host !== host) return prev;
          return {
            syntaxValid: true,
            nsResolved: ok,
            checking: false,
            host,
            message: ok ? '' : CORPORATE_DOMAIN_DNS_HINT,
          };
        });
      });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [query, previewModule]);

  useEffect(() => {
    if (previewModule !== TAB_EMAIL || !query.trim()) {
      setEmailDomainCheck({
        nsResolved: null,
        checking: false,
        host: '',
        message: '',
      });
      return undefined;
    }

    const host = emailDomainFromAddress(query);
    if (!host || !isValidDomainHost(host)) {
      setEmailDomainCheck({
        nsResolved: null,
        checking: false,
        host: '',
        message: '',
      });
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setEmailDomainCheck({
        nsResolved: null,
        checking: true,
        host,
        message: '',
      });
      checkDomainNS(host).then((ok) => {
        setEmailDomainCheck((prev) => {
          if (prev.host !== host) return prev;
          return {
            nsResolved: ok,
            checking: false,
            host,
            message: ok ? '' : EMAIL_DOMAIN_DNS_HINT,
          };
        });
      });
    }, 450);

    return () => window.clearTimeout(timer);
  }, [query, previewModule]);

  const onHeroSubmit = async ({ query: q, tab, personTags, companyTags }) => {
    const resolved = tab ?? detectInput(q)?.tab;
    if (!resolved) return;
    if (heroSubmitInFlightRef.current) return;
    heroSubmitInFlightRef.current = true;

    let submitQuery = String(q || '').trim();
    let launchPersonTags = personTags || null;
    let launchCompanyTags = companyTags || null;
    if (resolved === TAB_PERSON || resolved === TAB_COMPANY) {
      const mat = materializeHeroPills({
        force: true,
        draft: pillDraftRef.current,
        module: resolved,
      });
      if (mat?.composedQuery) submitQuery = mat.composedQuery;
      if (mat?.kind === 'person' && mat?.tags) launchPersonTags = mat.tags;
      if (mat?.kind === 'company' && mat?.tags) launchCompanyTags = mat.tags;
    }
    if (!submitQuery) {
      heroSubmitInFlightRef.current = false;
      return;
    }

    const dnsVerifiedHosts = [];
    try {
      beginSearchTrace({ query: submitQuery, tab: resolved, source: 'hero_go' });

      if (resolved === TAB_EMAIL) {
        const host = emailDomainFromAddress(submitQuery);
        if (host && isValidDomainHost(host)) {
          if (emailDomainCheck.host === host && emailDomainCheck.nsResolved === false) {
            markSearchTrace('dns_check_done', { detail: 'blocked: no NS records', ms: undefined });
            setEmailDomainCheck((prev) => ({
              ...prev,
              message: EMAIL_DOMAIN_DNS_HINT,
            }));
            return;
          }
          markSearchTrace('dns_check_start');
          const ok =
            emailDomainCheck.host === host && emailDomainCheck.nsResolved === true
              ? true
              : await checkDomainNS(host);
          markSearchTrace('dns_check_done', { detail: ok ? 'ok' : 'no NS records' });
          if (!ok) {
            setEmailDomainCheck({
              nsResolved: false,
              checking: false,
              host,
              message: EMAIL_DOMAIN_DNS_HINT,
            });
            return;
          }
          dnsVerifiedHosts.push(host);
          setEmailDomainCheck({
            nsResolved: true,
            checking: false,
            host,
            message: '',
          });
        }
      }

      if (resolved === TAB_COMPANY) {
        const host =
          launchCompanyTags?.domain || companyDomainFromQuery(submitQuery);
        if (host) {
          if (companyDomainCheck.host === host && companyDomainCheck.nsResolved === false) {
            markSearchTrace('dns_check_done', { detail: 'blocked: no NS records', ms: undefined });
            setCompanyDomainCheck((prev) => ({
              ...prev,
              message: CORPORATE_DOMAIN_DNS_HINT,
            }));
            return;
          }
          markSearchTrace('dns_check_start');
          const ok =
            companyDomainCheck.host === host && companyDomainCheck.nsResolved === true
              ? true
              : await checkDomainNS(host);
          markSearchTrace('dns_check_done', { detail: ok ? 'ok' : 'no NS records' });
          if (!ok) {
            setCompanyDomainCheck({
              syntaxValid: true,
              nsResolved: false,
              checking: false,
              host,
              message: CORPORATE_DOMAIN_DNS_HINT,
            });
            return;
          }
          dnsVerifiedHosts.push(host);
          setCompanyDomainCheck({
            syntaxValid: true,
            nsResolved: true,
            checking: false,
            host,
            message: '',
          });
        }
      }

      ws.launchScanFromHero(resolved, submitQuery, {
        dnsVerifiedHosts,
        respectManualTab: isManualOverride,
        personTags: launchPersonTags,
        companyTags: launchCompanyTags,
      });
    } finally {
      heroSubmitInFlightRef.current = false;
    }
  };

  // Deep-link autorun: new browser tab opens with ?autorun=1&q=...&module=...
  const autorunConsumedRef = useRef(false);
  useEffect(() => {
    if (autorunConsumedRef.current) return;
    const parsed = parseInvestigationDeepLink(window.location.search);
    if (!parsed?.query) return;
    autorunConsumedRef.current = true;
    try {
      window.history.replaceState({}, '', window.location.pathname || '/');
    } catch {
      /* ignore */
    }
    const module = parsed.module;
    syncQueryDetection(parsed.query);
    if (module === TAB_COMPANY) {
      const domain = parsed.domain || companyDomainFromQuery(parsed.query);
      const name = parsed.name || '';
      setIsManualOverride(true);
      setModuleOverride(TAB_COMPANY);
      onHeroSubmit({
        query: parsed.query,
        tab: TAB_COMPANY,
        companyTags: {
          companyName: name || parsed.query,
          domain,
          locationTags: Array.isArray(parsed.locations) ? parsed.locations : [],
        },
      });
      return;
    }
    if (module === TAB_PERSON && parsed.name) {
      onHeroSubmit({
        query: parsed.query,
        tab: TAB_PERSON,
        personTags: {
          name: parsed.name,
          contextTags: [],
          locationTags: Array.isArray(parsed.locations) ? parsed.locations : [],
          usernameTags: [],
        },
      });
      return;
    }
    onHeroSubmit({ query: parsed.query, tab: module });
    // Intentionally once on mount for deep-link handoff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectModule = (tabId) => {
    setModuleOverride(tabId);
    setIsManualOverride(true);

    // Single insufficient term (e.g. "Razvan"): clear the bar so the module
    // placeholder example is visible (Person / Corporate / Email / Phone).
    const pills = heroPillsRef.current;
    const kind = heroPillKindRef.current;
    const draft = String(pillDraftRef.current || '').trim();
    let composed = String(queryRef.current || '').trim();
    if (Array.isArray(pills) && pills.length > 0) {
      const base =
        kind === 'company'
          ? composeQueryFromCorporatePills(pills)
          : composeQueryFromPills(pills);
      composed = normalizeWhitespaceJoin(base, draft);
    } else if (draft) {
      composed = normalizeWhitespaceJoin(composed, draft);
    }

    if (!isInsufficientSingleTermQuery(composed)) return;

    clearHeroPills();
    setQuery('');
    queryRef.current = '';
    setDetection(null);
    setCompanyDomainCheck({
      syntaxValid: null,
      nsResolved: null,
      checking: false,
      host: '',
      message: '',
    });
    setEmailDomainCheck({
      nsResolved: null,
      checking: false,
      host: '',
      message: '',
    });
  };

  const resultsFeedRef = useRef(null);
  const mainScrollRef = useRef(null);
  const statsBarRef = useRef(null);
  const wasLoadingRef = useRef(false);
  const workspaceExitTimerRef = useRef(null);
  const [metricsInView, setMetricsInView] = useState(true);
  const [workspaceExiting, setWorkspaceExiting] = useState(false);
  // Keep compact chrome mounted through the exit fade so the feed does not unmount instantly.
  const compact = ws.showResultsArea || workspaceExiting;
  const collapseModules = ws.loading || ws.showResultsArea || workspaceExiting;
  const showResultsPanel = ws.showResultsArea || workspaceExiting;

  // Single scroll reset when scan ends (done) — never follow each SSE card.
  useEffect(() => {
    const finished = !ws.loading && wasLoadingRef.current && ws.showResultsArea;
    wasLoadingRef.current = ws.loading;
    if (!finished) return;
    const root = mainScrollRef.current;
    if (!root) return;
    requestAnimationFrame(() => {
      root.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [ws.loading, ws.showResultsArea]);

  // Full timer under search while docked; on scroll swap to FloatingScanHud (all viewports).
  useEffect(() => {
    if (!ws.showResultsArea) {
      setMetricsInView(true);
      return undefined;
    }
    const root = mainScrollRef.current;
    if (!root) return undefined;

    const syncMetricsVisibility = () => {
      // Any intentional scroll docks the full strip and reveals the mini HUD.
      if (root.scrollTop > 10) {
        setMetricsInView(false);
        return;
      }
      const metrics = statsBarRef.current;
      if (!metrics) {
        setMetricsInView(true);
        return;
      }
      const sticky = root.querySelector('.home-sticky-search-chrome');
      const stickyBottom = sticky ? sticky.getBoundingClientRect().bottom : 0;
      const rect = metrics.getBoundingClientRect();
      const stillVisible = rect.bottom - stickyBottom > Math.max(8, rect.height * 0.2);
      setMetricsInView(stillVisible);
    };

    syncMetricsVisibility();
    root.addEventListener('scroll', syncMetricsVisibility, { passive: true });
    window.addEventListener('resize', syncMetricsVisibility);
    return () => {
      root.removeEventListener('scroll', syncMetricsVisibility);
      window.removeEventListener('resize', syncMetricsVisibility);
    };
  }, [ws.showResultsArea, ws.loading]);

  const canReset = ws.hasAnyOutput || ws.hasWorkspaceInput || Boolean(query.trim());

  const clearHeroState = () => {
    setQuery('');
    queryRef.current = '';
    setModuleOverride(null);
    setIsManualOverride(false);
    setDetection(null);
    clearHeroPills();
  };

  useEffect(
    () => () => {
      if (workspaceExitTimerRef.current) {
        clearTimeout(workspaceExitTimerRef.current);
        workspaceExitTimerRef.current = null;
      }
    },
    [],
  );

  /**
   * Fade the results feed out (150ms) before clearing workspace data so Clear/Cancel
   * does not instantly tear down the DOM.
   *
   * @param {() => void} next
   * @param {{ scrollTop?: boolean }} [opts]
   */
  const runWorkspaceExit = (next, opts = {}) => {
    if (workspaceExiting) return;
    const shouldFade = ws.showResultsArea;
    if (!shouldFade) {
      next();
      if (opts.scrollTop) {
        mainScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    setWorkspaceExiting(true);
    if (workspaceExitTimerRef.current) {
      clearTimeout(workspaceExitTimerRef.current);
    }
    workspaceExitTimerRef.current = setTimeout(() => {
      workspaceExitTimerRef.current = null;
      next();
      setWorkspaceExiting(false);
      if (opts.scrollTop) {
        mainScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 150);
  };

  const handleReset = () => {
    runWorkspaceExit(() => {
      ws.handleResetClick({
        hasHeroQuery: Boolean(query.trim()),
        clearHeroQuery: clearHeroState,
      });
    });
  };

  const handleLogoReset = () => {
    runWorkspaceExit(
      () => {
        ws.resetWorkspaceFully({ clearHeroQuery: clearHeroState });
      },
      { scrollTop: true },
    );
  };

  return (
    <section
      className={`home-page-shell relative h-[100dvh] min-h-[100dvh] min-w-0 overflow-x-hidden overflow-hidden bg-slate-950 font-sans text-sm text-slate-100 ${
        compact ? '' : 'home-page-shell--idle'
      }`}
    >
      <WiwInvestigationHeader onLogoClick={handleLogoReset} />
      <FloatingScanHud
        active={ws.loading}
        resultsCount={ws.resultsCount}
        observeTargetRef={statsBarRef}
        metricsInView={metricsInView}
        onCancel={ws.abortScan}
      />

      <main
        ref={mainScrollRef}
        className="home-cyber-root home-premium-main relative z-0 min-h-0 min-w-0 overflow-x-clip overflow-y-auto overscroll-y-contain custom-scrollbar"
      >
        {/*
          Sonar at z-0; sticky chrome + opaque cards elevate themselves so the
          hairline is masked only on card faces (visible in gutters / feed gaps).
        */}
        <ScanRadarOverlay active={ws.loading} />
        <section
          className={`home-workspace-pad relative mx-auto flex w-full flex-col items-center transition-[transform,padding,margin,min-height] duration-[350ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-[transform,padding,margin] ${
            compact
              ? 'max-w-6xl min-h-0 justify-start px-4 py-3 pb-6 sm:px-8 sm:py-5 sm:pb-8'
              : 'home-hero-shell antialiased max-w-none h-full min-h-0 justify-center overflow-y-auto px-4 py-3 sm:px-6 sm:py-8 lg:px-6'
          }`}
        >
          <div
            className={
              compact
                ? 'w-full transition-[transform,padding,margin] duration-[350ms] ease-[cubic-bezier(0.16,1,0.3,1)]'
                : 'home-hero-cluster mx-auto flex w-full min-w-0 max-w-4xl flex-col items-center px-2 text-center transition-[transform,padding,margin] duration-[350ms] ease-[cubic-bezier(0.16,1,0.3,1)] sm:px-6 lg:w-full lg:max-w-4xl xl:max-w-4xl'
            }
          >
            {!compact ? (
              <header className="home-hero-copy w-full text-center">
                <h1 className="my-1 bg-gradient-to-r from-slate-100 via-white to-cyan-300 bg-clip-text font-['Plus_Jakarta_Sans',Inter,ui-sans-serif,system-ui,sans-serif] text-xl font-extrabold tracking-tight text-transparent sm:my-0 sm:text-3xl md:text-5xl">
                  OSINT Core
                </h1>
                <p className="mt-1.5 px-2 text-xs font-medium tracking-wide text-slate-300 sm:px-0 md:text-sm">
                  Enterprise Digital Due Diligence &amp; Threat Assessment Workspace
                </p>
              </header>
            ) : null}

            <section
              className={`home-sticky-search-chrome z-30 w-full min-w-0 will-change-[transform,padding,margin] transition-[transform,padding,margin,border-radius,background-color,box-shadow,border-color] duration-[350ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
                compact || collapseModules
                  ? 'sticky top-0 mt-1 border-b border-slate-800 bg-slate-950 px-0 py-1 sm:mt-2 sm:py-1.5'
                  : 'relative mt-6 rounded-2xl border border-slate-800 bg-slate-900/80 p-3 sm:mt-8 sm:p-4'
              } ${
                ws.loading
                  ? 'ring-1 ring-cyan-500/30 shadow-[0_0_24px_rgba(34,211,238,0.12)]'
                  : ''
              }`}
            >
              <div className="w-full min-w-0">
                <div className="home-landscape-search-row flex w-full min-w-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={handleLogoReset}
                    className="home-landscape-inline-logo hidden h-10 shrink-0 items-center rounded-lg border border-transparent px-1"
                    aria-label="Cyber Tracer — reset workspace"
                    data-testid="home-landscape-inline-logo"
                  >
                    <img
                      src={cyberTracerLogo}
                      alt=""
                      className="h-7 w-auto max-w-[7.5rem] object-contain object-left"
                      draggable={false}
                    />
                  </button>
                  <div className="home-landscape-search-slot min-w-0 flex-1">
                    <HomeHeroStrip
                      query={query}
                      onQueryChange={handleQueryChange}
                      activeModule={previewModule}
                      loading={ws.loading}
                      canReport={ws.canReport}
                      onSubmitSearch={onHeroSubmit}
                      onCancel={ws.abortScan}
                      onOpenReport={() => ws.setIsReportModalOpen(true)}
                      canReset={canReset}
                      onReset={handleReset}
                      compactChrome={Boolean(compact || collapseModules)}
                      companyDomainCheck={companyDomainCheck}
                      heroPills={heroPills}
                      heroPillKind={heroPillKind}
                      pillDraft={pillDraft}
                      onPillDraftChange={handlePillDraftChange}
                      onMaterializePills={materializeHeroPills}
                      onCyclePillType={handleCyclePillType}
                      onSetPillType={handleSetPillType}
                      onRemovePill={handleRemovePill}
                      emailDomainCheck={
                        emailDomainCheck.message
                          ? emailDomainCheck
                          : ws.currentData?.inputError && previewModule === TAB_EMAIL
                            ? {
                                nsResolved: false,
                                checking: false,
                                host: '',
                                message: ws.currentData.inputError,
                              }
                            : emailDomainCheck
                      }
                    />
                  </div>
                </div>

                <HomeModuleGrid
                  activeModuleId={previewModule}
                  onSelectModule={onSelectModule}
                  collapsed={collapseModules}
                  dense={Boolean(compact || collapseModules || ws.showResultsArea)}
                />
              </div>
            </section>

            {!compact ? (
              <section
                className="home-workspace-guidance my-4 flex w-full flex-col items-center md:my-6"
                aria-label="Workspace guidance"
              >
                <p className="max-w-2xl px-1 text-center text-sm font-medium leading-relaxed text-slate-300">
                  Public-data only. Results inform risk decisions; they are not endorsements or legal
                  findings.
                </p>
                <ul
                  className="mt-2.5 flex max-w-full flex-wrap items-center justify-center gap-1.5"
                  aria-label="OSINT compliance signals"
                >
                  {[
                    'Local AML & Sanctions Screening',
                    'Passive Footprint Gathering',
                    'Privacy-oriented',
                  ].map((label) => (
                    <li
                      key={label}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-800/80 bg-slate-900/50 px-2.5 py-0.5 text-[10px] font-medium text-slate-300"
                    >
                      <span className="text-emerald-400" aria-hidden>
                        ✓
                      </span>
                      {label}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>

          {showResultsPanel ? (
            <InvestigationResultsPanel
              ref={resultsFeedRef}
              activeTab={ws.activeTab}
              displayResults={ws.displayResults}
              loading={ws.loading}
              currentData={ws.currentData}
              getDisplayName={ws.getDisplayName}
              personCategoryFilter={ws.personCategoryFilter}
              onPersonCategoryFilter={ws.setPersonCategoryFilter}
              onEmailAnalysis={ws.handleLeadEmailAnalysis}
              exiting={workspaceExiting}
              statsBar={
                <div ref={statsBarRef} data-testid="home-feed-metrics-anchor">
                  <InvestigationStatsBar
                    className="home-feed-metrics mb-2 mt-0"
                    sticky={false}
                    loading={ws.loading}
                    penalties={ws.stats.penalties}
                    displayResults={ws.displayResults}
                    resultsCount={ws.resultsCount}
                    hasVerifiedIdentity={ws.hasVerifiedIdentity}
                    scanFinished={ws.scanFinishedRadar}
                    finalReport={ws.currentData.completed && !ws.loading}
                    trustScore={ws.stats.score}
                    trustLabel={ws.stats.label}
                    trustColorClass={ws.stats.colorClass}
                    logs={ws.currentData.logs}
                    logEndRef={ws.logEndRef}
                  />
                </div>
              }
              onPivotToPerson={(opts) => {
                const name = String(opts?.personName || '').trim();
                if (!name) return;
                const locTags = (Array.isArray(opts?.locations) ? opts.locations : [])
                  .map((t) => String(t || '').trim())
                  .filter(Boolean);
                const employer = String(opts?.companyName || '').trim();
                const ctxTags = [
                  ...(Array.isArray(opts?.contextTags) ? opts.contextTags : []),
                  employer,
                ]
                  .map((t) => String(t || '').trim())
                  .filter(Boolean)
                  .filter((t, i, arr) => arr.findIndex((x) => x.toLowerCase() === t.toLowerCase()) === i);
                const pills = buildPersonPillsFromTags({
                  name,
                  locationTags: locTags,
                  contextTags: ctxTags,
                });
                setIsManualOverride(true);
                setModuleOverride(TAB_PERSON);
                if (pills.length) {
                  applyHeroPills(pills, 'person');
                } else {
                  syncQueryDetection(name);
                }
                ws.handlePivotToPersonSearch(opts);
              }}
              onPivotLead={(pivot) => {
                const opts = deepLinkOptsFromPivot(pivot || {});
                if (!opts?.query) return;
                setIsManualOverride(true);
                setModuleOverride(opts.module);
                if (opts.module === TAB_PERSON) {
                  const handle = String(opts.handle || '').trim().replace(/^@+/, '');
                  const name = String(opts.name || opts.query || '').trim();
                  const pills = buildPersonPillsFromTags({
                    name,
                    locationTags: Array.isArray(opts.locations) ? opts.locations : [],
                    contextTags: [],
                    usernameTags: handle ? [handle] : [],
                  });
                  if (pills.length) {
                    applyHeroPills(pills, 'person');
                  } else {
                    syncQueryDetection(opts.query);
                  }
                } else if (opts.module === TAB_COMPANY) {
                  const domain = String(opts.domain || '').trim();
                  const companyName = String(opts.name || opts.query || '').trim();
                  const pills = buildCorporatePillsFromQuery(
                    [domain, companyName].filter(Boolean).join(' ') || opts.query,
                  );
                  if (pills?.length) {
                    applyHeroPills(pills, 'company');
                  } else {
                    syncQueryDetection(opts.query);
                  }
                } else {
                  syncQueryDetection(opts.query);
                }
                ws.handleDiscoveryPivotLead(pivot);
              }}
            />
          ) : null}
        </section>
      </main>

      <Footer className="relative z-20 shrink-0" dense={Boolean(compact || collapseModules)} />

      <ReportModal
        isOpen={ws.isReportModalOpen}
        onClose={() => ws.setIsReportModalOpen(false)}
        results={ws.scanResultsForReview}
        logs={ws.currentData.logs}
        target={ws.reportTarget}
        moduleName={ws.activeTab}
        getDisplayName={ws.getDisplayName}
        onExcludeFromFeed={ws.excludeResultFromFeed}
        extractedLeads={
          ws.activeTab === 'phone' || ws.activeTab === 'person'
            ? ws.currentData.extractedLeads
            : undefined
        }
      />
    </section>
  );
}
